import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { getMockLearningAlertDetails } from '../../../../mocks/mockRadarData';
import analyticsService from '../../../../services/analyticsService';
import type { LearningAlertDetail } from '../../../../types/radar';
import { KC_DETAIL_SOURCE } from '../../../../types/studentKcProgress.constants';
import { formatLocations } from '../../../../utils/radarUtils';
import { buildStudentKcProgressUrl } from '../../../../utils/studentKcProgressUtils';

type UseLearningBlockContentProps = {
  annotationId: string;
  isMockMode?: boolean;
};

type UseLearningBlockContentReturn = {
  detail: LearningAlertDetail | null;
  error: string | null;
  handleKcStatementClick: () => void;
  handleToggleRollbackInfo: () => void;
  handleToggleTimeline: () => void;
  hasRollbacks: boolean;
  hasTimeline: boolean;
  isLoading: boolean;
  isRollbackInfoExpanded: boolean;
  isTimelineExpanded: boolean;
  locationItems: string[];
  t: ReturnType<typeof useTranslation>['t'];
};

function useLearningBlockContent({ annotationId, isMockMode }: UseLearningBlockContentProps): UseLearningBlockContentReturn {
  const { i18n, t } = useTranslation(['chatbot']);
  const history = useHistory();
  const [detail, setDetail] = useState<LearningAlertDetail | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRollbackInfoExpanded, setIsRollbackInfoExpanded] = useState<boolean>(false);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (!annotationId) return;

    if (isMockMode) {
      const mockDetail = getMockLearningAlertDetails(i18n.language)[annotationId] ?? null;
      setDetail(mockDetail);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchDetail(): Promise<void> {
      setIsLoading(true);
      setError(null);

      try {
        const data = await analyticsService.getAnnotationDetail(annotationId);
        if (!cancelled) {
          setDetail(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          setDetail(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchDetail();

    return () => {
      cancelled = true;
    };
  }, [annotationId, isMockMode, i18n.language]);

  const handleToggleRollbackInfo = useCallback(() => {
    setIsRollbackInfoExpanded((prev) => !prev);
  }, []);

  const handleToggleTimeline = useCallback(() => {
    setIsTimelineExpanded((prev) => !prev);
  }, []);

  const handleKcStatementClick = useCallback(() => {
    if (!detail) return;
    const { kcId, studentExternalId } = detail;
    if (!studentExternalId || !kcId) return;
    history.push(buildStudentKcProgressUrl({ kcId, source: KC_DETAIL_SOURCE.RADAR, userId: studentExternalId }));
  }, [detail, history]);

  const hasRollbacks = detail ? detail.rollbackCount > 0 : false;
  const locationItems = useMemo(() => (detail ? formatLocations(detail.locations) : []), [detail]);

  const hasTimeline = (detail?.kcStatusTimeline.length ?? 0) > 0;

  return {
    detail,
    error,
    handleKcStatementClick,
    handleToggleRollbackInfo,
    handleToggleTimeline,
    hasRollbacks,
    hasTimeline,
    isLoading,
    isRollbackInfoExpanded,
    isTimelineExpanded,
    locationItems,
    t,
  };
}

export default useLearningBlockContent;
